import { useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NavigateFunction } from "react-router-dom";
import toast from "react-hot-toast";
import { AuthClient } from "@anythink-cloud/sdk";

import { userService } from "@/services/breeze/UserService";
import { tenantService } from "@/services/breeze/TenantService";
import { authService } from "@/services/breeze/AuthService";
import { MyUser } from "@/types/breeze/User";
import { Tenant } from "@/types/breeze/Tenant";
import { TENANT_ID_KEY } from "@/lib/auth";
import { config } from "@/config/env";
import { getOrgIdFromPath, signOutTargetPath } from "./useAuthRouting";

interface UseTenantAuthProps {
  pathname: string;
  navigate: NavigateFunction;
  authClient: AuthClient;
  setLoading: (loading: boolean) => void;
}

export function useTenantAuth({
  pathname,
  navigate,
  authClient,
  setLoading,
}: UseTenantAuthProps) {
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState<MyUser | null>(null);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const currentTenantRef = useRef<Tenant | null>(null);

  const isTenantAdmin = Boolean(currentUser?.is_tenant_administrator);

  const clearTenantState = useCallback(() => {
    setCurrentUser(null);
    setCurrentTenant(null);
    currentTenantRef.current = null;
    authClient.signOut();
    queryClient.clear();
  }, [queryClient, authClient]);

  const signOut = useCallback(
    async (errorMessage?: string) => {
      const orgId = currentTenantRef.current?.id || getOrgIdFromPath(pathname);
      try {
        await authService.signOut(authClient.getRefreshToken() || "");
        const { error } = await authClient.signOut();
        if (error) throw error;
      } catch (error) {
        console.error("[AuthProvider] Error signing out:", error);
      } finally {
        clearTenantState();
        if (window.parent !== window.self) {
          window.parent.postMessage({ type: "exit-management" }, "*");
        } else if (orgId) {
          navigate(`/org/${orgId}/auth/${signOutTargetPath(errorMessage)}`);
        } else {
          // Fallback to central auth if no org context
          window.location.href = config.centralAuthUrl;
        }
      }
    },
    [clearTenantState, pathname, navigate, authClient],
  );

  const handleTenant = useCallback(
    async (targetOrgId?: string) => {
      try {
        const userDetails: MyUser = await userService.getMyUser();

        // Breeze UI is restricted to central users only
        if (!userDetails.is_central_user) {
          toast.error(
            "Access Denied: this is restricted to central users only. Please contact your administrator if you believe this is an error.",
          );
          await signOut();
          return;
        }

        setCurrentUser(userDetails);

        let desiredOrgId = targetOrgId ?? null;
        if (!desiredOrgId) {
          // try and pull it from the URL
          desiredOrgId = getOrgIdFromPath(pathname);
        }

        if (desiredOrgId) {
          const selectedTenant = await tenantService.getTenantById(
            Number(desiredOrgId),
          );
          if (selectedTenant) {
            setCurrentTenant(selectedTenant);
            currentTenantRef.current = selectedTenant;
            localStorage.setItem(TENANT_ID_KEY, selectedTenant.id.toString());
            return;
          }
          toast.error("You don't have access to this Organisation.");
          console.warn(
            "[AuthProvider] User does not have access to organisation",
            { desiredOrgId },
          );
          signOut("no_access_to_org");
          return;
        } else {
          console.error("[AuthProvider] No org found in path or targetOrgId", {
            pathname,
            targetOrgId,
          });
          signOut();
        }
      } catch (error) {
        console.error("[AuthProvider] Error fetching user data:", error);
        // When the OAuth integration callback opens a relay-only tab via
        // window.open, that tab has no Breeze session — /users/me 401s and
        // this catch block fires. The tab is about to close itself after
        // posting code+state to the opener; the toast just adds noise.
        const isOAuthRelayTab =
          pathname.includes("/settings/integrations/callback") &&
          typeof window !== "undefined" &&
          !!window.opener &&
          !window.opener.closed;
        if (!isOAuthRelayTab) {
          toast.error("There was an error logging you in. Please try again.");
        }
        await signOut();
      } finally {
        setLoading(false);
      }
    },
    [pathname, signOut, setLoading],
  );

  return {
    currentUser,
    currentTenant,
    isTenantAdmin,
    handleTenant,
    signOut,
  };
}
